import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { ShowcaseLineUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type ShowcaseLineUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  concept: string;
  conceptSelectedValue?: { displayId: string; id: string } | null;
  onConceptChange?: (value: string) => void;
  conceptError?: string;
  quantity: number;
  quantitySelectedValue?: { displayId: string; id: string } | null;
  onQuantityChange?: (value: number) => void;
  quantityError?: string;
  unitPrice: number;
  unitPriceSelectedValue?: { displayId: string; id: string } | null;
  onUnitPriceChange?: (value: number) => void;
  unitPriceError?: string;
  note: string;
  noteSelectedValue?: { displayId: string; id: string } | null;
  onNoteChange?: (value: string) => void;
  noteError?: string;
  showcaseLineUpdateStore: ShowcaseLineUpdateStore;
};

export const ShowcaseLineUpdateForm: React.FC<ShowcaseLineUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      concept = "",
      conceptError,
      onConceptChange = () => {},
      quantity = 0,
      quantityError,
      onQuantityChange = () => {},
      unitPrice = 0,
      unitPriceError,
      onUnitPriceChange = () => {},
      note = "",
      noteError,
      onNoteChange = () => {},
      showcaseLineUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="ShowcaseLine"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "concept":
                    onConceptChange?.(valor);
                    break;
                  case "quantity":
                    onQuantityChange?.(valor);
                    break;
                  case "unitPrice":
                    onUnitPriceChange?.(valor);
                    break;
                  case "note":
                    onNoteChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={showcaseLineUpdateStore.getCardErrors("null")}
              title={langStore.groupShowcaseLineLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={concept}
                  error={Boolean(conceptError)}
                  warning={
                    !conceptError &&
                    Boolean(showcaseLineUpdateStore.warnings["concept"])
                  }
                  helperText={
                    conceptError ||
                    showcaseLineUpdateStore.warnings["concept"]?.message
                  }
                  label={langStore.ShowcaseLineConceptLabel}
                  onChange={(value: string) => onConceptChange(value)}
                />
                <InputNumber
                  value={quantity}
                  error={Boolean(quantityError)}
                  warning={
                    !quantityError &&
                    Boolean(showcaseLineUpdateStore.warnings["quantity"])
                  }
                  helperText={
                    quantityError ||
                    showcaseLineUpdateStore.warnings["quantity"]?.message
                  }
                  label={langStore.ShowcaseLineQuantityLabel}
                  decimalScale={0}
                  onChange={(value: number) => onQuantityChange(value)}
                />
                <InputNumber
                  value={unitPrice}
                  error={Boolean(unitPriceError)}
                  warning={
                    !unitPriceError &&
                    Boolean(showcaseLineUpdateStore.warnings["unitPrice"])
                  }
                  helperText={
                    unitPriceError ||
                    showcaseLineUpdateStore.warnings["unitPrice"]?.message
                  }
                  label={langStore.ShowcaseLineUnitPriceLabel}
                  decimalScale={2}
                  prefix={"$"}
                  thousandSeparator={","}
                  fixedDecimalScale={true}
                  onChange={(value: number) => onUnitPriceChange(value)}
                />
                <InputString
                  value={note}
                  error={Boolean(noteError)}
                  warning={
                    !noteError &&
                    Boolean(showcaseLineUpdateStore.warnings["note"])
                  }
                  helperText={
                    noteError ||
                    showcaseLineUpdateStore.warnings["note"]?.message
                  }
                  label={langStore.ShowcaseLineNoteLabel}
                  onChange={(value: string) => onNoteChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
